<!-- Hero Banner Section -->
<section
  class="relative overflow-hidden bg-slate-900 text-white py-12 lg:py-16 border-b border-slate-800"
  aria-labelledby="live-classes-heading"
>
  <!-- Subtle Background Gradient Overlay -->
  <div
    class="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-600/30 via-purple-600/20 to-transparent pointer-events-none"
  ></div>

  <div class="container mx-auto px-6 relative z-10">
    <!-- Breadcrumb -->
    <div class="mb-4">
      <div class="daisy-breadcrumbs text-xs sm:text-sm text-slate-400">
        <ul>
          <li>
            <a
              routerLink="/"
              class="hover:text-blue-400 flex items-center gap-1.5"
            >
              <i class="fa-solid fa-house text-xs"></i> Home
            </a>
          </li>
          <li class="text-slate-200 font-semibold">
            <i class="fa-solid fa-video text-xs text-blue-400 mr-1.5"></i>
            Live Classes
          </li>
        </ul>
      </div>
    </div>

    <!-- Title & Stats Split Layout -->
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
      <div class="lg:col-span-8 space-y-4">
        <div
          class="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-400/30 text-blue-300 px-4 py-1.5 rounded-full text-xs font-medium"
        >
          <span class="relative flex h-2 w-2">
            <span
              class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"
            ></span>
            <span
              class="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"
            ></span>
          </span>
          <span>Live Zoom Interactive Sessions</span>
        </div>
        <h1
          id="live-classes-heading"
          class="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight"
        >
          Live Interactive Learning
        </h1>
        <p
          class="text-slate-300 text-base sm:text-lg max-w-2xl leading-relaxed"
        >
          Join thousands of learners in live interactive Zoom sessions with
          expert teachers. Get instant feedback, ask questions in real-time, and
          accelerate your learning journey.
        </p>

        <!-- Trust Highlights -->
        <div
          class="flex flex-wrap items-center gap-6 pt-2 text-xs sm:text-sm text-slate-300"
        >
          <div class="flex items-center gap-2">
            <i class="fa-solid fa-video text-blue-400"></i>
            <span>Zoom Live Streaming</span>
          </div>
          <div class="flex items-center gap-2">
            <i class="fa-solid fa-comments text-purple-400"></i>
            <span>Real-Time Q&A</span>
          </div>
          <div class="flex items-center gap-2">
            <i class="fa-solid fa-clock-rotate-left text-emerald-400"></i>
            <span>Class Replays Included</span>
          </div>
        </div>
      </div>

      <!-- Quick Platform Counter Badges -->
      <div class="lg:col-span-4 flex flex-wrap lg:justify-end gap-3">
        <div
          class="bg-slate-800/80 border border-slate-700/80 p-5 rounded-2xl text-center shadow-lg min-w-[140px]"
        >
          <p class="text-3xl font-extrabold text-blue-400">
            {{ pagination.totalDocs }}
          </p>
          <p
            class="text-xs text-slate-400 flex items-center justify-center gap-1.5 mt-1"
          >
            <i class="fa-solid fa-chalkboard text-blue-400"></i> Active Classes
          </p>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Main Content Section -->
<section class="py-12 bg-base-200/50 min-h-screen text-base-content">
  <div class="container mx-auto px-6">
    <!-- Results Header Bar -->
    <div
      class="flex justify-between items-center mb-8 flex-wrap gap-4 daisy-card daisy-card-bordered bg-base-100 border-base-300 p-4 rounded-2xl shadow-sm"
    >
      <div>
        <h2 class="text-xl font-bold text-base-content m-0">
          Available Live Classes
        </h2>
        <p class="text-xs sm:text-sm text-base-content/70 m-0 mt-0.5">
          Showing {{ pagination.pagingCounter }}-{{
            pagination.pagingCounter + pagination.limit - 1 >
            pagination.totalDocs
              ? pagination.totalDocs
              : pagination.pagingCounter + pagination.limit - 1
          }}
          of {{ pagination.totalDocs }} active classes
        </p>
      </div>
    </div>

    <!-- Classes Grid -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
      @for (card of liveClassCards; track card.id) {
        <div
          class="h-full transition-transform duration-200 hover:-translate-y-1 p-2 flex flex-col justify-between"
        >
          <black-ui-card
            [data]="card"
            (clickMoreDetails)="onClickLiveClass($event)"
            (clickContinueLearning)="OnClickContinue($event)"
            (clickAccessResources)="onClickAccessResources($event)"
            (clickJoinMeetings)="onClickJoinMeetings($event)"
            (clickEnroll)="onClickSubscribe($event)"
          ></black-ui-card>
        </div>
      } @empty {
        <!-- Empty State -->
        <div
          class="col-span-full text-center py-16 px-6 daisy-card daisy-card-bordered bg-base-100 rounded-2xl border-base-300 shadow-sm space-y-4 max-w-lg mx-auto"
        >
          <div
            class="w-16 h-16 mx-auto bg-base-200 rounded-full flex items-center justify-center border border-base-300 text-base-content/40"
          >
            <i class="fa-solid fa-video-slash text-3xl"></i>
          </div>
          <h3 class="text-xl font-bold text-base-content m-0">
            No Live Classes Found
          </h3>
          <p class="text-sm text-base-content/70 m-0">
            We couldn't find any active live classes matching your query right
            now. Please check back later.
          </p>
        </div>
      }
    </div>

    <!-- Enhanced Pagination -->
    <div
      *ngIf="pagination.totalPages > 1"
      class="daisy-card daisy-card-bordered bg-base-100 rounded-2xl p-4 sm:p-6 shadow-sm border-base-300 max-w-3xl mx-auto"
    >
      <div class="flex justify-between items-center flex-wrap gap-4">
        <div class="text-sm text-base-content/70 font-medium">
          Page {{ pagination.page }} of {{ pagination.totalPages }}
        </div>

        <nav class="flex items-center gap-1.5 sm:gap-2" aria-label="Pagination Navigation">
          <!-- Previous Button -->
          <button
            class="daisy-btn daisy-btn-sm daisy-btn-outline gap-1 rounded-xl disabled:opacity-50"
            [disabled]="!pagination.hasPrevPage"
            (click)="paginateItems(pagination.page - 1)"
          >
            <i class="fa-solid fa-chevron-left text-xs"></i>
            <span class="hidden sm:inline">Previous</span>
          </button>

          <!-- Page Numbers -->
          @for (
            item of createRange(pagination.totalPages, pagination.page);
            track item
          ) {
            @if (item >= pagination.page - 2 && item <= pagination.page + 2) {
              <button
                class="daisy-btn daisy-btn-sm rounded-xl font-bold"
                [class.daisy-btn-primary]="pagination.page === item"
                [class.daisy-btn-ghost]="pagination.page !== item"
                (click)="paginateItems(item)"
              >
                {{ item }}
              </button>
            }
          }

          <!-- Next Button -->
          <button
            class="daisy-btn daisy-btn-sm daisy-btn-outline gap-1 rounded-xl disabled:opacity-50"
            [disabled]="!pagination.hasNextPage"
            (click)="paginateItems(pagination.page + 1)"
          >
            <span class="hidden sm:inline">Next</span>
            <i class="fa-solid fa-chevron-right text-xs"></i>
          </button>
        </nav>
      </div>
    </div>
  </div>
</section>
